import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useFlags, useLDClient, useLDClientError, withLDProvider } from 'launchdarkly-react-client-sdk';
import { CustomerType } from '@lendi/analytics-web';
import { Type, useSession } from '@lendi/lala-react';

import { LAUNCH_DARKLY_CLIENT_ID, LD_TARGET_ID_LOCAL_STORAGE_KEY } from '@funnels/constants';
import { buildTracking, cookieStorageUtil, getBrand, getViewType, lendiAnalytics } from '@funnels/utils';

interface LaunchDarklyConsumerProps {
  children?: React.ReactNode;
}

interface AnalyticsIdentifierProps {
  uid: string;
  utype: CustomerType;
}
function AnalyticsIdentifier({ uid, utype }: AnalyticsIdentifierProps) {
  const allFlags = useFlags();
  const { analytics } = useAnalytics();
  useEffect(() => {
    analytics?.identify(uid, allFlags, {
      user_type: utype,
    }); // analytics identify call
  }, [uid, utype, allFlags, analytics]);
  return null;
}
const tokenAnalyticsUserTypeMapping = {
  [Type.Team]: CustomerType.TEAM,
  [Type.Customer]: CustomerType.CUSTOMER,
};

function LaunchDarklyConsumer(props: LaunchDarklyConsumerProps): JSX.Element {
  /* SYNC OPERATIONS */
  const targetId = cookieStorageUtil.get(LD_TARGET_ID_LOCAL_STORAGE_KEY) || crypto.randomUUID();
  const isTeamView = getViewType() === Type.Team;
  const brand = getBrand();
  const custom = { targetId, isTeamView, brand };
  /* HOOKS THAT RETURN ONE VALUE EXACTLY AND ALWAYS (at least for TV) */
  const { identity, type } = useSession();
  const ldClient = useLDClient();
  const ldError = useLDClientError();

  /* This is going to run straight on mount 
     We do need to wait for this to complete to prevent flickering
     It should be really fast though
  */
  useEffect(() => {
    if (!ldClient) return;
    // This line is responsible to control ff targetting (e.g., for testing a feature behind a ff for one QA/Dev only)
    // Warning: This call can cause application re-renders (or flickering) when feature-flags change upon identifing!
    // This is only relevant for targeted feature flags though and doesn't apply to 99% of users
    // Just something for Devs & QAs to be aware!
    ldClient.identify(identity ? { key: identity.id, custom } : { anonymous: true, custom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ldClient]);

  if (ldError || Object.keys(ldClient?.allFlags() ?? {}).length > 0)
    return (
      <AnalyticsProvider>
        <>
          <AnalyticsIdentifier uid={identity?.id ?? ''} utype={tokenAnalyticsUserTypeMapping[type]} />
          {props.children}
        </>
      </AnalyticsProvider>
    );

  return <span data-error="Something went wrong: LaunchDarklyConsumer"></span>;
}

const LDProvider = withLDProvider({
  clientSideID: LAUNCH_DARKLY_CLIENT_ID,
  options: {
    bootstrap: 'localStorage',
    // we don't care about LD analytics events - we don't use them
    sendEvents: true,
    // we don't care about LD goals api - we don't use it
    fetchGoals: true,
  },
})(LaunchDarklyConsumer);

export default LDProvider;

export type AnalyticsContextType = {
  analytics: ReturnType<typeof lendiAnalytics> | null;
  tracking: ReturnType<typeof buildTracking> | null;
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const flags = useFlags();
  const [analytics, setAnalytics] = useState<ReturnType<typeof lendiAnalytics> | null>(null);
  const [tracking, setTracking] = useState<ReturnType<typeof buildTracking> | null>(null);

  useEffect(() => {
    if (flags['analyticsWebV4'] !== undefined) {
      const instance = lendiAnalytics(flags['analyticsWebV4']);
      setAnalytics(instance);
      setTracking(buildTracking(instance));
    }
  }, [flags]);

  const value = useMemo<AnalyticsContextType>(() => ({ analytics, tracking }), [analytics, tracking]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
